import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AuthDivider } from './AuthCard';

/**
 * Single sign-on, offered only where it actually works.
 *
 * The API is asked which providers this deployment has credentials for. A
 * deployment with none renders nothing at all — a "Continue with Google"
 * button that fails when pressed is worse than no button, because the person
 * who presses it has no way to tell whether the fault is theirs.
 */

interface Provider {
  id: string;
  label: string;
}

/** Google's mark, drawn rather than fetched — the CSP admits no image hosts. */
function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" className="h-[18px] w-[18px]" aria-hidden focusable="false">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.91c1.7-1.57 2.69-3.88 2.69-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.91-2.26c-.81.54-1.84.86-3.05.86-2.34 0-4.33-1.58-5.04-3.71H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.96 10.71a5.41 5.41 0 0 1 0-3.42V4.96H.96a9 9 0 0 0 0 8.08l3-2.33Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.59C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3 2.33C4.67 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

export function SsoButtons() {
  const { data } = useQuery({
    queryKey: ['auth', 'sso-providers'],
    queryFn: async () => {
      const { data: result } = await api.get<{ providers: Provider[] }>('/auth/sso/providers');
      return result.providers;
    },
    // The answer is deployment configuration; it does not change while
    // somebody is looking at the login page.
    staleTime: Infinity,
    retry: false,
  });

  if (!data || data.length === 0) return null;

  return (
    <div className="mb-6 space-y-5">
      <div className="space-y-2.5">
        {data.map((provider) => (
          <a
            key={provider.id}
            /**
             * A real link, not a fetch.
             *
             * The whole exchange is a sequence of top-level redirects — here,
             * to Google, and back to the API callback, which sets the session
             * cookie. An XHR could not follow any of it.
             */
            href={`${api.defaults.baseURL ?? ''}/auth/sso/${provider.id}/start`}
            className="flex h-[46px] w-full items-center justify-center gap-2.5 rounded-xl border bg-card text-[14px] font-medium transition-colors hover:bg-accent"
          >
            {provider.id === 'google' && <GoogleMark />}
            Continue with {provider.label}
          </a>
        ))}
      </div>

      <AuthDivider label="or" />
    </div>
  );
}

/**
 * The callback redirects here with a reason code rather than a sentence, so
 * the wording lives with the rest of the interface copy.
 *
 * Each one says what to do next. "Something went wrong" on a sign-in page
 * leaves somebody with no move except to try the same thing again.
 */
const SSO_ERRORS: Record<string, string> = {
  sso_not_configured: 'Single sign-on is not set up for this workspace. Sign in with your email.',
  sso_state_mismatch: 'That sign-in link has expired. Please try again.',
  sso_exchange_failed: 'Google could not complete the sign-in. Please try again.',
  sso_bad_token: 'Google could not complete the sign-in. Please try again.',
  sso_email_unverified:
    'That Google account has not verified its email address. Verify it with Google, or sign in with your password.',
  sso_domain_not_allowed: 'Use your Petpooja work account to sign in.',
  sso_no_account:
    'No Petpooja account uses that Google address. Ask your administrator to add you, then try again.',
  sso_account_disabled: 'Your account has been disabled. Contact your administrator.',
};

export function ssoErrorMessage(code: string | null): string | null {
  if (!code) return null;
  return SSO_ERRORS[code] ?? 'Sign-in could not be completed. Please try again.';
}
